import jsQR from 'jsqr';
import { DataTable } from '../components/shared/DataTable';
import { DateInput } from '../components/shared/DateInput';
import { useDateTime } from '../hooks/useDateTime';
import { localIsoDate } from '../utils/local-date';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Field, Input, Select, Text, Textarea, makeStyles, tokens } from '@fluentui/react-components';
import { GateFace } from '../components/GateFace';
import { useT } from '../i18n';
import { usePermissions } from '../hooks/usePermission';
import { attendanceErrorMessage } from '../utils/attendance-error';
import {
  confirmGate, fetchAccessConfig, fetchAccessEvents, lookupGate, registerVehiclePass, saveVehicle,
  type AccessDirection, type AccessSource, type GateView,
  authorizeAccessEntry,
} from '../api/access.api';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  card: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalM, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'flex-end' },
  muted: { color: tokens.colorNeutralForeground3 },
  video: { width: '100%', maxWidth: '420px', borderRadius: tokens.borderRadiusMedium },
  people: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: tokens.spacingHorizontalS },
  person: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalXXS, textAlign: 'center' },
  photo: { width: '96px', height: '96px', objectFit: 'cover', borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3 },
  big: { fontSize: tokens.fontSizeHero700, fontWeight: 700 },
});

/** QR y códigos de barras comunes; BarcodeDetector ignora los que el equipo no soporte al construirlo con getSupportedFormats. */
const CODE_FORMATS = ['qr_code', 'code_128', 'code_39', 'code_93', 'codabar', 'ean_13', 'ean_8', 'itf', 'upc_a', 'upc_e', 'data_matrix', 'pdf417'];

const RESULT_COLOR = { MATCH: 'success', MISMATCH: 'danger', ALLOWED: 'success', VISITOR: 'warning', DENIED: 'danger', PENDING: 'warning', AUTHORIZED: 'success' } as const;

type BarcodeDetectorLike = { detect: (v: HTMLVideoElement) => Promise<{ rawValue: string }[]> };
const detectorCtor = (): (new (o: { formats: string[] }) => BarcodeDetectorLike) | undefined =>
  (window as unknown as { BarcodeDetector?: new (o: { formats: string[] }) => BarcodeDetectorLike }).BarcodeDetector;

/** Caseta: verifica el pase de lista de una cuadrilla (lector o cámara) y registra vehículos a mano. */
export function GatePage() {
  const t = useT();
  const g = t.gatePage;
  const fmt = useDateTime();
  const s = useStyles();
  const qc = useQueryClient();
  const { permissions } = usePermissions();
  const canAuthorize = permissions.has('attendance:enroll');

  const [code, setCode] = useState('');
  const [source, setSource] = useState<AccessSource>('SCANNER');
  const [view, setView] = useState<GateView | null>(null);
  const [counted, setCounted] = useState('');
  const [direction, setDirection] = useState<AccessDirection>('IN');
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  const [v, setV] = useState({ plate: '', driver: '', company: '', occupants: '', note: '', direction: 'IN' as AccessDirection, validUntil: '' });
  const cfg = useQuery({ queryKey: ['access-config'], queryFn: fetchAccessConfig, retry: false });
  const scanMode = cfg.data?.scanMode ?? 'BOTH';
  const gateOff = (cfg.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error === 'FEATURE_DISABLED';
  const showReader = scanMode !== 'CAMERA';
  const showCamera = scanMode !== 'SCANNER';
  const events = useQuery({ queryKey: ['access-events'], queryFn: () => fetchAccessEvents(localIsoDate()), retry: false });
  const refreshLog = () => qc.invalidateQueries({ queryKey: ['access-events'] });

  const lookup = useMutation({
    mutationFn: (c: string) => lookupGate(c),
    onSuccess: (r) => { setView(r); setCounted(''); setMsg(null); },
    onError: () => { setView(null); setMsg({ text: g.invalidCode, ok: false }); },
  });
  const search = useCallback((c: string, src: AccessSource) => {
    const clean = c.trim();
    if (!clean) return;
    setSource(src); setCode(clean); lookup.mutate(clean);
  }, [lookup]);

  const confirm = useMutation({
    mutationFn: () => confirmGate(code, { counted: Number(counted), direction, source }),
    onSuccess: (r) => {
      setMsg(r.result === 'MATCH' ? { text: g.match, ok: true } : { text: g.mismatch(r.expected, r.counted), ok: false });
      setView(null); setCode(''); void refreshLog(); codeInput.current?.focus();
    },
    onError: (e) => setMsg({ text: attendanceErrorMessage(e, t.attendancePage.errors, t.attendancePage.genericError), ok: false }),
  });

  const pass = useMutation({
    mutationFn: () => registerVehiclePass({
      plate: v.plate, direction: v.direction, driver: v.driver || undefined, company: v.company || undefined,
      occupants: v.occupants ? Number(v.occupants) : undefined, note: v.note || undefined,
    }),
    onSuccess: (r) => { setMsg({ text: r.result === 'ALLOWED' ? g.allowed : g.visitor, ok: r.result === 'ALLOWED' }); setV({ ...v, plate: '', driver: '', company: '', occupants: '', note: '' }); void refreshLog(); },
    onError: () => setMsg({ text: g.invalidVehicle, ok: false }),
  });
  const authorizeEntry = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => authorizeAccessEntry(id, reason),
    onSuccess: () => { setMsg({ text: g.authorizedOk, ok: true }); void refreshLog(); },
    onError: (e) => setMsg({ text: attendanceErrorMessage(e, t.attendancePage.errors, t.attendancePage.genericError), ok: false }),
  });
  const authorize = useMutation({
    mutationFn: () => saveVehicle({ plate: v.plate, driver: v.driver || undefined, company: v.company || undefined, validUntil: v.validUntil || undefined }),
    onSuccess: () => setMsg({ text: g.saved, ok: true }),
    onError: (e) => setMsg({ text: attendanceErrorMessage(e, t.attendancePage.errors, t.attendancePage.genericError), ok: false }),
  });

  // Cámara: lee el QR con BarcodeDetector (Chrome/Android); si el navegador no lo trae, se avisa.
  useEffect(() => {
    if (!cameraOn) return;
    // BarcodeDetector no existe en Chrome de escritorio (Windows/Linux): se lee con jsQR sobre un lienzo.
    const Ctor = detectorCtor();
    const detector = Ctor ? new Ctor({ formats: CODE_FORMATS }) : null;
    const canvas = document.createElement('canvas');
    const readFrame = async (el: HTMLVideoElement): Promise<string | undefined> => {
      if (detector) return (await detector.detect(el))[0]?.rawValue;
      if (!el.videoWidth) return undefined;
      const w = Math.min(640, el.videoWidth);
      canvas.width = w; canvas.height = Math.round((el.videoHeight * w) / el.videoWidth);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return undefined;
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      return jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' })?.data;
    };
    let stop = false;
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout>;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (stop || !video.current) { stream.getTracks().forEach((x) => x.stop()); return; }
        video.current.srcObject = stream;
        await video.current.play();
        // La cámara queda abierta: lee QR y códigos de barras sola; el rostro se identifica con el botón del mismo panel.
        let lastCode = ''; let lastAt = 0;
        const tick = async () => {
          if (stop || !video.current) return;
          try {
            const raw = showCamera ? await readFrame(video.current) : undefined;
            if (raw && (raw !== lastCode || Date.now() - lastAt > 4000)) { lastCode = raw; lastAt = Date.now(); search(raw, 'CAMERA'); }
          } catch { /* cuadro sin imagen: se reintenta */ }
          timer = setTimeout(tick, 300);
        };
        void tick();
      } catch { setMsg({ text: g.cameraUnsupported, ok: false }); setCameraOn(false); }
    })();
    return () => { stop = true; clearTimeout(timer); stream?.getTracks().forEach((x) => x.stop()); };
  }, [cameraOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirSelect = (value: AccessDirection, on: (d: AccessDirection) => void) => (
    <Select value={value} onChange={(_, d) => on(d.value as AccessDirection)}>
      <option value="IN">{g.entry}</option><option value="OUT">{g.exit}</option>
    </Select>
  );

  if (gateOff) return <div className={s.page}><Text>{t.attendancePage.disabled}</Text></div>;

  return (
    <div className={s.page}>
      <div><Text size={600} weight="semibold" block>{g.title}</Text><Text className={s.muted}>{g.subtitle}</Text></div>
      {msg && <Text role="status" weight="semibold" style={{ color: msg.ok ? tokens.colorPaletteGreenForeground1 : tokens.colorPaletteRedForeground1 }}>{msg.text}</Text>}

      <section className={s.card} aria-label={g.people}>
        <Text weight="semibold">{g.people}</Text>
        <form className={s.row} onSubmit={(e) => { e.preventDefault(); search(code, 'SCANNER'); }}>
          {showReader && (
            <>
              <Field label={g.codeLabel} hint={g.codeHint}>
                <Input ref={codeInput} autoFocus value={code} autoComplete="off" onChange={(_, d) => setCode(d.value)} />
              </Field>
              <Button appearance="primary" type="submit" disabled={lookup.isPending}>{g.search}</Button>
            </>
          )}
          <Button onClick={() => setCameraOn((x) => !x)}>{cameraOn ? g.cameraOff : g.camera}</Button>
        </form>
        {view && (
          <div className={s.card}>
            {view.group && <Text>{g.group}: <b>{view.group}</b></Text>}
            {view.closedAt && <Text className={s.muted}>{g.closedAt}: {fmt.dateTime(view.closedAt)}</Text>}
            <Text>{g.present}</Text>
            <Text className={s.big}>{view.present}</Text>
            {view.people?.some((x) => x.status === 'INACTIVE') && (
              <Text role="alert" weight="semibold" style={{ color: tokens.colorPaletteRedForeground1 }}>
                {g.inactiveInList.replace('{n}', String(view.people.filter((x) => x.status === 'INACTIVE').length))}
              </Text>
            )}
            {view.people ? (
              <div className={s.people}>
                {view.people.map((x) => (
                  <div key={x.employeeId} className={s.person}>
                    {x.photoUrl ? <img src={x.photoUrl} alt={x.name} className={s.photo} /> : <div className={s.photo} aria-hidden />}
                    <Text size={200} weight="semibold">{x.name}</Text>
                    <Badge appearance="tint" color={x.status === 'INACTIVE' ? 'danger' : x.status === 'PRE_TERMINATION' ? 'warning' : 'success'}>{g.status[x.status]}</Badge>
                  </div>
                ))}
                {view.extras?.map((x, i) => (
                  <div key={`x${i}`} className={s.person}>
                    <div className={s.photo} aria-hidden />
                    <Text size={200} weight="semibold">{x.name}</Text>
                    <Badge appearance="tint" color="informative">{g.status.EXTRA}</Badge>
                  </div>
                ))}
              </div>
            ) : <Text className={s.muted}>{view.names.join(', ')}</Text>}
            <div className={s.row}>
              <Field label={g.countedLabel}>
                <Input type="number" inputMode="numeric" min={0} value={counted} onChange={(_, d) => setCounted(d.value)} />
              </Field>
              {dirSelect(direction, setDirection)}
              <Button appearance="primary" disabled={counted === '' || confirm.isPending} onClick={() => confirm.mutate()}>{g.confirm}</Button>
            </div>
          </div>
        )}
      </section>

      {cameraOn && (
        <section className={s.card} aria-label={g.cameraTitle}>
          <Text className={s.muted}>{g.cameraHint}</Text>
          <video ref={video} className={s.video} muted playsInline />
          <GateFace video={video} active={cameraOn} onLogged={(text, ok) => { setMsg({ text, ok }); void refreshLog(); }} />
        </section>
      )}

      <section className={s.card} aria-label={g.vehicles}>
        <Text weight="semibold">{g.vehicles}</Text>
        <div className={s.row}>
          <Field label={g.plate}><Input value={v.plate} onChange={(_, d) => setV({ ...v, plate: d.value })} /></Field>
          <Field label={g.driver}><Input value={v.driver} onChange={(_, d) => setV({ ...v, driver: d.value })} /></Field>
          <Field label={g.company}><Input value={v.company} onChange={(_, d) => setV({ ...v, company: d.value })} /></Field>
          <Field label={g.occupants}><Input type="number" min={0} max={200} value={v.occupants} onChange={(_, d) => setV({ ...v, occupants: d.value })} /></Field>
          {dirSelect(v.direction, (d) => setV({ ...v, direction: d }))}
        </div>
        <Field label={g.note}><Textarea value={v.note} onChange={(_, d) => setV({ ...v, note: d.value })} /></Field>
        <div className={s.row}>
          <Button appearance="primary" disabled={v.plate.trim().length < 4 || Number(v.occupants) > 200 || pass.isPending} onClick={() => pass.mutate()}>{g.register}</Button>
          {canAuthorize && (
            <>
              <Field label={g.validUntil}><DateInput value={v.validUntil} onValueChange={(iso) => setV({ ...v, validUntil: iso })} /></Field>
              <Button disabled={v.plate.trim().length < 4 || authorize.isPending} onClick={() => authorize.mutate()}>{g.save}</Button>
            </>
          )}
        </div>
      </section>

      <section className={s.card} aria-label={g.logTitle}>
        <Text weight="semibold">{g.logTitle}</Text>
        {(events.data?.length ?? 0) === 0 ? <Text className={s.muted}>{g.emptyLog}</Text> : (
          <DataTable
            rows={events.data!}
            rowKey={(e) => e.entryId}
            columns={[
              { key: 'time', header: g.colTime, render: (e) => `${e.at ? fmt.time(e.at) : ''} ${e.direction === 'OUT' ? g.exit : g.entry}` },
              { key: 'type', header: g.colType, render: (e) => e.kind === 'VEHICLE' ? g.typeVehicle : e.kind === 'PERSON' ? g.typePerson : g.typeList },
              { key: 'detail', header: g.colDetail, width: 1.6, render: (e) => e.kind === 'VEHICLE' ? [e.plate, e.driver, e.company].filter(Boolean).join(' · ') : e.kind === 'PERSON' ? [e.name, e.similarity ? `${e.similarity}%` : ''].filter(Boolean).join(' · ') : `${e.counted}/${e.expected}` },
              { key: 'result', header: g.colResult, width: 1.4, render: (e) => (
                <>
                  {e.result && <Badge appearance="tint" color={RESULT_COLOR[e.result]}>{g.results[e.result]}</Badge>}
                  {e.reason && <Text size={200} block>{e.reason}</Text>}
                  {e.result === 'PENDING' && !e.resolvedBy && cfg.data?.canAuthorize && (
                    <Button size="small" onClick={() => { const r = window.prompt(g.authReason); if (r && r.trim().length >= 3) authorizeEntry.mutate({ id: e.entryId, reason: r.trim() }); }}>{g.authorizePass}</Button>
                  )}
                  {e.result === 'PENDING' && e.resolvedBy && <Text size={200} block>{g.resolved}</Text>}
                </>
              ) },
            ]}
          />
        )}
      </section>
    </div>
  );
}
